/**
 * Chart symbols for buoyage and lights, generated as small canvas images and
 * supplied to MapLibre on demand via the `styleimagemissing` event. The buoy
 * layer references an image id of the form `chart-mark-<category>` (e.g.
 * `chart-mark-port`); the first time the map needs one we draw it here.
 *
 * Buoyage is drawn in one of three symbology themes (the selectable symbol set):
 *   - 'current'  our simplified rendering (the original look);
 *   - 'paper'    the traditional paper-chart / INT1 look (floating marks sloped,
 *                with the small position ring at the base);
 *   - 'ecdis'    the ENC / ECDIS / S-52 simplified look (a common upright pillar
 *                body, the topmark carrying the mark's meaning).
 *
 * Everything is driven by the real feature attribute `category` the chart
 * converter normalises from the S-57 source (CATLAM / CATCAM / object class), and
 * the colours and topmarks are the standard IALA region A symbols. These are OUR
 * renderings of the international standard symbols; we don't copy any UKHO
 * NP5011 / NP5012 publication artwork or proprietary glyph files.
 *
 * Honest note on topmarks: the cells carry no separate TOPMAR object, but the
 * topmark for a cardinal / safe-water / isolated-danger / special mark is defined
 * by the mark's category in IALA, so drawing it from `category` is correct
 * portrayal, not invented data.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import { isStyleReady } from '../styleReady';
import type { GlyphStyle } from './symbolSets';
import { STANDARD_ASSETS } from './standardAssets.generated';

const S = 44; // canvas px

const RED = '#d11b2f';
const GREEN = '#0a8a2a';
const YELLOW = '#f4c20a';
const BLACK = '#1d1d1d';
const WHITE = '#ffffff';
const GREY = '#7a7a7a';
const LIGHTMAG = '#cc1f8f'; // S-52 light magenta
const AMBER = '#e8920a';

type Ctx = CanvasRenderingContext2D;

/** The buoyage glyph style currently in force; set by setChartSymbolStyle. */
let currentGlyphStyle: GlyphStyle = 'current';

// --- Genuine standard assets (S-52 PresLib / NOAA) ---------------------------
// For the 'paper' and 'ecdis' sets we draw the REAL standard glyph from a
// pre-decoded asset when one exists, falling back to the canvas drawing below.
// Assets decode asynchronously; until ready (and always under test, where image
// decoding is unavailable) the canvas fallback is used, so rendering never breaks.
type Rgba = { width: number; height: number; data: Uint8Array };
const decodedAssets = new Map<string, Rgba>();
let assetsPreloadedFor: MaplibreMap | null = null;
let reskinTimer: ReturnType<typeof setTimeout> | null = null;

function standardGlyph(id: string, style: GlyphStyle): Rgba | null {
  if (style === 'current') return null;
  // Mark/beacon ids may carry a charted-colour suffix (~<colours>). The genuine
  // assets have the IALA region-A paint baked in, so when the charted colour
  // DISAGREES with the category default (region B: a green port-hand can), the
  // asset would be the wrong colour — return null and let the canvas renderer
  // draw the true paint. When it agrees (or no colour is charted) the asset is
  // right, and we look it up under the id without the suffix.
  const tilde = id.indexOf('~');
  if (tilde >= 0) {
    const payload = id.replace(/^chart-(mark|beacon)-/, '');
    const { category, colours } = parseMarkId(payload);
    const charted = bandsFromColour(colours);
    const dflt = BANDS[category];
    if (charted && (!dflt || charted.join() !== dflt.join())) return null;
    id = id.slice(0, tilde);
  }
  // Buoy ids may carry a body-shape suffix (chart-mark-<cat>--<shape>).
  const mk = /^chart-mark-(.+?)--(.+)$/.exec(id);
  if (mk) {
    // Paper has genuine SHAPE-DRIVEN composed buoys (genuine NOAA body shape +
    // per-category colour + topmark) keyed by category--shape; prefer that so the
    // drawn shape follows the actual BOYSHP. Fall back to the per-category crop
    // when no shape-specific asset exists. ECDIS uses its per-category S-52 glyph
    // (simplified by design), so it always strips the shape.
    if (style === 'paper') {
      const exact = decodedAssets.get(`${id}|paper`);
      if (exact) return exact;
    }
    return decodedAssets.get(`chart-mark-${mk[1]}|${style}`) ?? null;
  }
  return decodedAssets.get(`${id}|${style}`) ?? null;
}

function preloadStandardAssets(map: MaplibreMap): void {
  if (assetsPreloadedFor === map) return;
  assetsPreloadedFor = map;
  if (typeof document === 'undefined' || typeof Image === 'undefined') return;
  for (const [id, byStyle] of Object.entries(STANDARD_ASSETS)) {
    for (const key of Object.keys(byStyle) as GlyphStyle[]) {
      const uri = byStyle[key];
      if (!uri) continue;
      const img = new Image();
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = S;
          c.height = S;
          const ctx = c.getContext('2d');
          if (!ctx) return;
          ctx.clearRect(0, 0, S, S);
          ctx.drawImage(img, 0, 0, S, S);
          decodedAssets.set(`${id}|${key}`, {
            width: S,
            height: S,
            data: new Uint8Array(ctx.getImageData(0, 0, S, S).data.buffer),
          });
          if (reskinTimer) clearTimeout(reskinTimer);
          reskinTimer = setTimeout(() => setChartSymbolStyle(map, currentGlyphStyle), 60);
        } catch {
          /* asset decode is best-effort; the canvas fallback covers it */
        }
      };
      img.src = uri;
    }
  }
}

// --- primitives --------------------------------------------------------------

/** A cone (triangle). `up` true = apex at top. */
function cone(ctx: Ctx, cx: number, top: number, bottom: number, halfW: number, up: boolean, fill: string) {
  ctx.beginPath();
  if (up) {
    ctx.moveTo(cx, top);
    ctx.lineTo(cx - halfW, bottom);
    ctx.lineTo(cx + halfW, bottom);
  } else {
    ctx.moveTo(cx - halfW, top);
    ctx.lineTo(cx + halfW, top);
    ctx.lineTo(cx, bottom);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = BLACK;
  ctx.stroke();
}

function circle(ctx: Ctx, cx: number, cy: number, r: number, fill: string) {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = BLACK;
  ctx.stroke();
}

/** A rectangular body filled with colour bands (top -> bottom, or left -> right). */
function bandedBody(ctx: Ctx, x: number, top: number, w: number, h: number, bands: string[], vertical = false) {
  if (vertical) {
    const bw = w / bands.length;
    bands.forEach((c, i) => {
      ctx.fillStyle = c;
      ctx.fillRect(x + i * bw, top, bw + 0.5, h);
    });
  } else {
    const bh = h / bands.length;
    bands.forEach((c, i) => {
      ctx.fillStyle = c;
      ctx.fillRect(x, top + i * bh, w, bh + 0.5);
    });
  }
  ctx.lineWidth = 1;
  ctx.strokeStyle = BLACK;
  ctx.strokeRect(x, top, w, h);
}

/** A cone-shaped (triangular) body filled with horizontal bands via clipping. */
function bandedCone(ctx: Ctx, cx: number, top: number, bottom: number, halfW: number, bands: string[]) {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(cx - halfW, bottom);
  ctx.lineTo(cx + halfW, bottom);
  ctx.closePath();
  ctx.clip();
  const bh = (bottom - top) / bands.length;
  bands.forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect(cx - halfW, top + i * bh, halfW * 2, bh + 0.5);
  });
  ctx.restore();
  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(cx - halfW, bottom);
  ctx.lineTo(cx + halfW, bottom);
  ctx.closePath();
  ctx.lineWidth = 1;
  ctx.strokeStyle = BLACK;
  ctx.stroke();
}

/** A round (spherical) body filled with horizontal colour bands via clipping. */
function bandedCircle(ctx: Ctx, cx: number, cy: number, r: number, bands: string[]) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  const bh = (2 * r) / bands.length;
  bands.forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect(cx - r, cy - r + i * bh, 2 * r, bh + 0.5);
  });
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.lineWidth = 1;
  ctx.strokeStyle = BLACK;
  ctx.stroke();
}

function saltire(ctx: Ctx, cx: number, cy: number, r: number, colour: string) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - r, cy - r);
  ctx.lineTo(cx + r, cy + r);
  ctx.moveTo(cx + r, cy - r);
  ctx.lineTo(cx - r, cy + r);
  ctx.stroke();
}

// --- per-category data -------------------------------------------------------

const CX = S / 2;
const BODY_TOP = 17;
const BODY_BOT = 33;
const BODY_MID = (BODY_TOP + BODY_BOT) / 2;
const BODY_W = 10;
const BODY_X = CX - BODY_W / 2;

/** Colour bands per IALA category, top -> bottom (safe water is vertical). */
const BANDS: Record<string, string[]> = {
  port: [RED],
  stbd: [GREEN],
  preferred_port: [RED, GREEN, RED],
  preferred_stbd: [GREEN, RED, GREEN],
  ncard: [BLACK, YELLOW],
  scard: [YELLOW, BLACK],
  ecard: [BLACK, YELLOW, BLACK],
  wcard: [YELLOW, BLACK, YELLOW],
  safe_water: [RED, WHITE, RED, WHITE],
  isolated_danger: [BLACK, RED, BLACK],
  special: [YELLOW],
};

const isCone = (category: string) => category === 'stbd' || category === 'preferred_stbd';
const isVertical = (category: string) => category === 'safe_water';

/**
 * S-57 COLOUR letter (the converter's attrs.colour, e.g. "R" or "G/R/G") ->
 * body fill. The letters are the standard COLOUR codes the ingest normalises
 * (W/B/R/G/Bu/Y/Gy/Br/Am/Vi/Or/Mg/Pk).
 */
const COLOUR_FILLS: Record<string, string> = {
  W: WHITE,
  B: BLACK,
  R: RED,
  G: GREEN,
  Bu: '#1f5fbf',
  Y: YELLOW,
  Gy: GREY,
  Br: '#7a4a1f',
  Am: AMBER,
  Or: '#e87511',
  Vi: '#7a3fbf',
  Mg: LIGHTMAG,
  Pk: '#e08aa8',
};

/**
 * The charted colour bands from an attrs.colour string ("R", "G/R/G", ...), or
 * null when any letter is unknown — an unknown paint falls back to the
 * category's default bands rather than guessing.
 *
 * WHY the charted colour outranks the category default: CATLAM gives
 * port/starboard by direction of buoyage, but the PAINT differs by IALA region.
 * Region A: port = red. Region B (the Americas): port = green. BANDS holds the
 * region-A convention, so drawing from category alone painted every US lateral
 * mark the wrong way round (a red can flashing Fl.G — the Ponce report). The
 * COLOUR attribute is the actual paint on the actual buoy, right in both
 * regions, and it also gets preferred-channel banding (RGR / GRG) exact.
 * Pure + exported for unit tests.
 */
export function bandsFromColour(colours: string | undefined): string[] | null {
  if (!colours) return null;
  const parts = colours.split('/').map((c) => c.trim()).filter(Boolean);
  if (!parts.length) return null;
  const bands: string[] = [];
  for (const p of parts) {
    const fill = COLOUR_FILLS[p];
    if (!fill) return null;
    bands.push(fill);
  }
  return bands;
}

/**
 * Split a mark image id's payload "category[--shape][~colours]" into its parts.
 * Pure + exported for unit tests.
 */
export function parseMarkId(mark: string): { category: string; shape?: string; colours?: string } {
  let rest = mark;
  let colours: string | undefined;
  const tilde = rest.indexOf('~');
  if (tilde >= 0) {
    colours = rest.slice(tilde + 1) || undefined;
    rest = rest.slice(0, tilde);
  }
  const sep = rest.indexOf('--');
  if (sep >= 0) return { category: rest.slice(0, sep), shape: rest.slice(sep + 2), colours };
  return { category: rest, colours };
}

/** Two cardinal cones at the top, in the arrangement for N/E/S/W. */
function cardinalTopmark(ctx: Ctx, type: 'n' | 'e' | 's' | 'w') {
  const halfW = 4;
  const up = { top: 2, bottom: 8 };
  const lo = { top: 8, bottom: 14 };
  const drawCone = (band: { top: number; bottom: number }, pointUp: boolean) =>
    cone(ctx, CX, pointUp ? band.top : band.bottom, pointUp ? band.bottom : band.top, halfW, pointUp, BLACK);
  if (type === 'n') {
    drawCone(up, true);
    drawCone(lo, true);
  } else if (type === 's') {
    drawCone(up, false);
    drawCone(lo, false);
  } else if (type === 'e') {
    drawCone(up, true);
    drawCone(lo, false);
  } else {
    drawCone(up, false);
    drawCone(lo, true);
  }
}

/**
 * The category's topmark, shared across all symbol styles. Lateral topmarks
 * take the mark's principal (top-band) colour when the charted colour is
 * known — a region-B green can carries a GREEN can topmark, not region A's red.
 */
function drawTopmark(ctx: Ctx, category: string, principal?: string) {
  switch (category) {
    case 'port':
    case 'preferred_port':
      bandedBody(ctx, CX - 4, 8, 8, 5, [principal ?? RED]); // can topmark, mark colour
      break;
    case 'stbd':
    case 'preferred_stbd':
      cone(ctx, CX, 7, 13, 4, true, principal ?? GREEN); // cone topmark, mark colour
      break;
    case 'ncard':
      cardinalTopmark(ctx, 'n');
      break;
    case 'scard':
      cardinalTopmark(ctx, 's');
      break;
    case 'ecard':
      cardinalTopmark(ctx, 'e');
      break;
    case 'wcard':
      cardinalTopmark(ctx, 'w');
      break;
    case 'safe_water':
      circle(ctx, CX, 9, 4, RED); // single red sphere
      break;
    case 'isolated_danger':
      circle(ctx, CX, 5.5, 3, BLACK); // two black spheres
      circle(ctx, CX, 12, 3, BLACK);
      break;
    case 'special':
      saltire(ctx, CX, 9, 4, YELLOW); // yellow saltire
      break;
    default:
      break;
  }
}

/**
 * The floating buoy body, banded by category and shaped by the S-57 body shape
 * (BOYSHP) when known: can, cone, spherical (a ball), pillar or spar. Falls back to
 * the IALA convention for the category (port = can, stbd = cone) when no shape is
 * given. This is what makes a spherical special mark draw as a ball, a cardinal as
 * a pillar, and so on, rather than every buoy as a can.
 */
function drawBodyCurrent(ctx: Ctx, category: string, shape?: string, colourBands?: string[] | null) {
  const bands = colourBands ?? BANDS[category];
  if (!bands) {
    circle(ctx, CX, BODY_MID, 5, GREY);
    return;
  }
  const eff = shape && shape !== 'def' ? shape : isCone(category) ? 'conical' : 'can';
  switch (eff) {
    case 'spherical':
      bandedCircle(ctx, CX, BODY_MID, 6, bands);
      break;
    case 'pillar':
    case 'super-buoy':
      bandedBody(ctx, CX - 3, 13, 6, BODY_BOT - 13, bands, isVertical(category));
      break;
    case 'spar':
      bandedBody(ctx, CX - 2, 12, 4, BODY_BOT - 12, bands, isVertical(category));
      break;
    case 'conical':
      bandedCone(ctx, CX, BODY_TOP, BODY_BOT, BODY_W / 2, bands);
      break;
    case 'can':
    case 'barrel':
    default:
      bandedBody(ctx, BODY_X, BODY_TOP, BODY_W, BODY_BOT - BODY_TOP, bands, isVertical(category));
  }
}

/** ECDIS / S-52 simplified body: one common upright pillar, the topmark gives type. */
function drawBodyEcdis(ctx: Ctx, category: string, colourBands?: string[] | null) {
  const bands = colourBands ?? BANDS[category];
  if (!bands) {
    circle(ctx, CX, BODY_MID, 5, GREY);
    return;
  }
  // A slim upright pillar (narrower + a touch taller than the can/cone bodies).
  bandedBody(ctx, CX - 3, 14, 6, 20, bands, isVertical(category));
}

/**
 * The topmark colour to use with a set of charted bands: the top band, except
 * white (a white top band's topmark stays the category default — a white can
 * topmark would vanish against the halo).
 */
function principalOf(bands: string[] | null): string | undefined {
  const top = bands?.[0];
  return top && top !== WHITE ? top : undefined;
}

/** Draw one buoyage symbol for "category[--shape][~colours]" + style. */
function draw(ctx: Ctx, mark: string, style: GlyphStyle) {
  const { category, shape, colours } = parseMarkId(mark);
  const colourBands = bandsFromColour(colours);
  const paper = style === 'paper';
  if (paper) {
    // Paper / INT1 convention: floating marks are drawn sloped to the right.
    ctx.save();
    const k = 0.16;
    ctx.transform(1, 0, -k, 1, k * BODY_MID, 0);
  }
  if (style === 'ecdis') drawBodyEcdis(ctx, category, colourBands);
  else drawBodyCurrent(ctx, category, shape, colourBands);
  drawTopmark(ctx, category, principalOf(colourBands));
  if (paper) ctx.restore();

  if (paper) {
    // The small open position ring at the base, the paper "exact position" mark.
    ctx.beginPath();
    ctx.arc(CX, BODY_BOT + 1.5, 2.2, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = BLACK;
    ctx.stroke();
  }

  // Position dot at the very bottom centre, the icon's bottom anchor.
  circle(ctx, CX, S - 4, 1.8, BLACK);
}

/**
 * A FIXED beacon (the BCN* object classes), as opposed to a floating buoy (BOY*).
 * Drawn as an upright banded triangle structure carrying the SAME topmark and
 * colour as the matching buoy, standing on a solid base foot. It is never sloped
 * and has no floating position ring, so it reads as a fixed mark: the topmark +
 * colour give the IALA category, the upright triangle + base foot give "fixed".
 * (For the ECDIS set the genuine S-52 beacon asset overrides this.)
 */
function drawBeacon(ctx: Ctx, mark: string) {
  const { category, colours } = parseMarkId(mark);
  const colourBands = bandsFromColour(colours);
  const bands = colourBands ?? BANDS[category] ?? [GREY];
  const apexY = 15;
  const baseY = BODY_BOT;
  const halfW = 5.5;
  // The beacon structure: an upright banded triangle (apex up), not a floating body.
  bandedCone(ctx, CX, apexY, baseY, halfW, bands);
  // Solid base foot — the fixed, driven-in indicator that separates a beacon from
  // a floating buoy.
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(CX - halfW - 1.5, baseY);
  ctx.lineTo(CX + halfW + 1.5, baseY);
  ctx.stroke();
  // The category's topmark, above the apex (identical to the matching buoy).
  drawTopmark(ctx, category, principalOf(colourBands));
  // Position dot at the bottom-centre anchor.
  circle(ctx, CX, S - 4, 1.8, BLACK);
}

/** Pick a flare tint from a light's colour letters (e.g. 'W', 'R', 'W/G'). */
function lightTint(colour: string): string {
  const c = colour.toUpperCase();
  if (c.includes('R')) return RED;
  if (c.includes('G')) return GREEN;
  if (c.includes('Y') || c.includes('O')) return AMBER;
  return LIGHTMAG;
}

/** Our teardrop flare, tinted by the light's colour (the Current set). */
function drawFlareCurrent(ctx: Ctx, colour: string) {
  const tint = lightTint(colour);
  const baseY = S - 4;
  ctx.save();
  ctx.translate(CX, baseY);
  ctx.rotate((-35 * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(-7, -16, 0, -26);
  ctx.quadraticCurveTo(7, -16, 0, 0);
  ctx.closePath();
  ctx.fillStyle = tint;
  ctx.globalAlpha = 0.92;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.lineWidth = 1;
  ctx.strokeStyle = BLACK;
  ctx.stroke();
  ctx.restore();
  circle(ctx, CX, baseY, 1.8, BLACK);
}

/**
 * ECDIS / S-52 light flare: a magenta teardrop rising from the light, the S-52
 * convention (the flare is magenta whatever the light's colour; the colour is
 * carried by the character text). Slightly slimmer than ours.
 */
function drawFlareEcdis(ctx: Ctx) {
  const baseY = S - 4;
  ctx.save();
  ctx.translate(CX, baseY);
  ctx.rotate((-45 * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(-5, -16, 0, -27);
  ctx.quadraticCurveTo(5, -16, 0, 0);
  ctx.closePath();
  ctx.fillStyle = LIGHTMAG;
  ctx.globalAlpha = 0.9;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.restore();
  circle(ctx, CX, baseY, 1.8, BLACK);
}

/**
 * The NP5011 light "flare": a magenta flame / teardrop rising up to the right
 * from the light position. Thin tapering tail at the base (the light), fat rounded
 * head at the tip — built as a head circle plus the tangent-line tail, so it reads
 * as the engraved comma/flame on a paper chart, not a star or asterisk.
 */
function paperFlare(ctx: Ctx, baseX: number, baseY: number, dist: number, headR: number) {
  const ang = -Math.PI / 4; // up and to the right
  const hx = baseX + Math.cos(ang) * dist;
  const hy = baseY + Math.sin(ang) * dist;
  const d = Math.hypot(hx - baseX, hy - baseY);
  const aCP = Math.atan2(baseY - hy, baseX - hx); // head -> base
  const ac = Math.acos(Math.min(1, headR / d));
  ctx.fillStyle = LIGHTMAG;
  // tail: the base point to the two tangent points on the head
  ctx.beginPath();
  ctx.moveTo(baseX, baseY);
  ctx.lineTo(hx + headR * Math.cos(aCP + ac), hy + headR * Math.sin(aCP + ac));
  ctx.lineTo(hx + headR * Math.cos(aCP - ac), hy + headR * Math.sin(aCP - ac));
  ctx.closePath();
  ctx.fill();
  // fat rounded head
  ctx.beginPath();
  ctx.arc(hx, hy, headR, 0, Math.PI * 2);
  ctx.fill();
}

/** A small black star marking a light position (NP5011 1.1 "position of light"). */
function paperLightStar(ctx: Ctx, cx: number, cy: number, r: number) {
  ctx.fillStyle = BLACK;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const rr = i % 2 === 0 ? r : r * 0.42;
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

/**
 * Traditional paper-chart (INT1) light flare (NP5011): the magenta flame from the
 * light position, with a small star marking the position. Standard magenta flare
 * whatever the light's colour — the colour and full character are read off the
 * separate character-text layer, as on paper.
 */
function drawFlarePaper(ctx: Ctx, _colour: string) {
  const baseY = S - 4;
  paperFlare(ctx, CX, baseY, 16, 5.5);
  paperLightStar(ctx, CX, baseY, 3);
}

/** A light flare in the active symbol set. */
function drawFlare(ctx: Ctx, colour: string, style: GlyphStyle) {
  if (style === 'paper') return drawFlarePaper(ctx, colour);
  if (style === 'ecdis') return drawFlareEcdis(ctx);
  return drawFlareCurrent(ctx, colour);
}

/** Major light, current look: a tapered tower with a lit lantern. */
function drawLighthouseCurrent(ctx: Ctx) {
  const cx = CX;
  const baseY = S - 5;
  ctx.beginPath();
  ctx.moveTo(cx - 6, baseY);
  ctx.lineTo(cx - 3.5, baseY - 20);
  ctx.lineTo(cx + 3.5, baseY - 20);
  ctx.lineTo(cx + 6, baseY);
  ctx.closePath();
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = BLACK;
  ctx.stroke();
  ctx.fillStyle = RED;
  ctx.fillRect(cx - 5, baseY - 14, 10, 4);
  ctx.beginPath();
  ctx.arc(cx, baseY - 22, 3, Math.PI, 0);
  ctx.closePath();
  ctx.fillStyle = LIGHTMAG;
  ctx.fill();
  ctx.strokeStyle = LIGHTMAG;
  ctx.lineWidth = 1.4;
  for (const a of [-40, 0, 40]) {
    const r = (a * Math.PI) / 180;
    ctx.beginPath();
    ctx.moveTo(cx + Math.sin(r) * 4, baseY - 24 - Math.cos(r) * 4);
    ctx.lineTo(cx + Math.sin(r) * 9, baseY - 24 - Math.cos(r) * 9);
    ctx.stroke();
  }
  circle(ctx, cx, baseY, 1.6, BLACK);
}

/**
 * Major light / lighthouse, traditional paper-chart (INT1, NP5011): a bold black
 * star marking the light position with the magenta flame flare coming off it.
 * That star-plus-flare IS the charted form — no tower structure, no yellow disc
 * (the disc is only the special "significant all-round light" case). The name and
 * character ("Lt Ho", the light characteristic) come from the text layer.
 */
function drawLighthousePaper(ctx: Ctx) {
  const cx = CX;
  const baseY = S - 6;
  paperFlare(ctx, cx, baseY, 19, 6.5);
  paperLightStar(ctx, cx, baseY, 6.4);
}

/**
 * Major light, ECDIS / S-52: a small black structure with a large magenta flare
 * (the S-52 major-light treatment, magenta whatever the light's colour; bigger
 * than a minor light's flare so a major light reads as major).
 */
function drawLighthouseEcdis(ctx: Ctx) {
  const cx = CX;
  const baseY = S - 5;
  ctx.save();
  ctx.translate(cx, baseY);
  ctx.rotate((-45 * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(-7, -20, 0, -34);
  ctx.quadraticCurveTo(7, -20, 0, 0);
  ctx.closePath();
  ctx.fillStyle = LIGHTMAG;
  ctx.globalAlpha = 0.92;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.restore();
  ctx.fillStyle = BLACK;
  ctx.fillRect(cx - 2.5, baseY - 5, 5, 5);
  circle(ctx, cx, baseY, 2.2, LIGHTMAG);
}

/**
 * A lighthouse / major light in the active symbol set. Current keeps the tower
 * with the lit lantern; Paper (INT1) shows the traditional magenta star/burst of
 * a major light; ECDIS (S-52) shows the large magenta S-52 flare.
 */
function drawLighthouse(ctx: Ctx, style: GlyphStyle) {
  if (style === 'paper') return drawLighthousePaper(ctx);
  if (style === 'ecdis') return drawLighthouseEcdis(ctx);
  return drawLighthouseCurrent(ctx);
}

/** An anchor, the chart symbol for an anchorage. Paper (INT1) draws it black;
 *  Current and ECDIS draw it magenta (S-52 magenta anchor). */
function drawAnchor(ctx: Ctx, style: GlyphStyle = 'current') {
  const cx = CX;
  const topY = 9;
  const botY = S - 9;
  const colour = style === 'paper' ? BLACK : LIGHTMAG;
  ctx.strokeStyle = colour;
  ctx.fillStyle = colour;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, topY, 3, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx, topY + 3);
  ctx.lineTo(cx, botY);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - 8, topY + 8);
  ctx.lineTo(cx + 8, topY + 8);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, botY - 10, 11, (20 * Math.PI) / 180, (160 * Math.PI) / 180);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - 10.3, botY - 6);
  ctx.lineTo(cx - 14, botY - 10);
  ctx.lineTo(cx - 12, botY - 2);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx + 10.3, botY - 6);
  ctx.lineTo(cx + 14, botY - 10);
  ctx.lineTo(cx + 12, botY - 2);
  ctx.closePath();
  ctx.fill();
}

// --- Hazard glyphs (wrecks, underwater rocks) per symbol set -----------------
// Centred glyphs (anchored centre by their layers). Current = our look; Paper =
// INT1 / Chart No.1; ECDIS = S-52. Our own renderings of the standard shapes.

function rockAsterisk(ctx: Ctx, cx: number, cy: number, r: number, colour: string) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  // Six rays (three crossing strokes) so it reads as a chart rock asterisk, not
  // an X (which would clash with the wreck cross).
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    ctx.stroke();
  }
}

function rockPlus(ctx: Ctx, cx: number, cy: number, r: number, colour: string) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - r, cy);
  ctx.lineTo(cx + r, cy);
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx, cy + r);
  ctx.stroke();
}

/** The dotted "danger" enclosure drawn round a dangerous rock/obstruction. */
function dangerRing(ctx: Ctx, cx: number, cy: number, rx: number, ry: number) {
  ctx.save();
  ctx.strokeStyle = BLACK;
  ctx.lineWidth = 1;
  ctx.setLineDash([1.5, 2]);
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Underwater rock awash / covering (dangerous): per-set. */
function drawRockAwash(ctx: Ctx, style: GlyphStyle) {
  const cx = CX;
  const cy = S / 2;
  if (style === 'ecdis') {
    rockPlus(ctx, cx, cy, 7, BLACK);
    dangerRing(ctx, cx, cy, 10, 8);
  } else if (style === 'paper') {
    rockAsterisk(ctx, cx, cy, 7, BLACK);
    dangerRing(ctx, cx, cy, 10, 8);
  } else {
    rockAsterisk(ctx, cx, cy, 7, BLACK);
  }
}

/** Underwater rock submerged (always covered): per-set. */
function drawRockSub(ctx: Ctx, style: GlyphStyle) {
  const cx = CX;
  const cy = S / 2;
  rockPlus(ctx, cx, cy, 6, style === 'paper' ? BLACK : GREY);
}

/** Sunken-hull "wreck" mark (NP5011/Chart No.1 K22-23): an elongated oval with
 *  diagonal hatching — the charted submerged-wreck symbol. */
function wreckHatchedOval(ctx: Ctx, cx: number, cy: number, colour: string) {
  const rx = 10;
  const ry = 4.4;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.6;
  ctx.stroke();
  ctx.clip();
  ctx.lineWidth = 1;
  for (let x = -2 * rx; x <= rx; x += 3.4) {
    ctx.beginPath();
    ctx.moveTo(cx + x, cy - ry - 2);
    ctx.lineTo(cx + x + (ry + 2) * 2, cy + ry + 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Solid hull silhouette (K24, hull/superstructure showing): a filled low hull. */
function wreckHull(ctx: Ctx, cx: number, cy: number, colour: string) {
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy - 1);
  ctx.lineTo(cx + 10, cy - 1);
  ctx.quadraticCurveTo(cx + 7, cy + 6, cx, cy + 6);
  ctx.quadraticCurveTo(cx - 7, cy + 6, cx - 10, cy - 1);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.4;
  ctx.lineCap = 'round';
  for (const dx of [-4, 4]) {
    ctx.beginPath();
    ctx.moveTo(cx + dx, cy - 1);
    ctx.lineTo(cx + dx, cy - 7);
    ctx.stroke();
  }
}

/** Masts-only symbol (K25): a short waterline with three masts. */
function wreckMastsOnly(ctx: Ctx, cx: number, cy: number, colour: string) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy + 2);
  ctx.lineTo(cx + 9, cy + 2);
  ctx.stroke();
  for (const dx of [-6, 0, 6]) {
    ctx.beginPath();
    ctx.moveTo(cx + dx, cy + 2);
    ctx.lineTo(cx + dx, cy - 7);
    ctx.stroke();
  }
}

/** A dotted oval enclosure (danger line / non-dangerous submerged ECDIS oval). */
function wreckDottedOval(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, colour: string) {
  ctx.save();
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1;
  ctx.setLineDash([1.5, 2]);
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** S-52 isolated-danger mark: a filled magenta circle with a white cross. */
function isolatedDangerMark(ctx: Ctx, cx: number, cy: number, r: number) {
  ctx.fillStyle = LIGHTMAG;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = WHITE;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  const d = r * 0.5;
  ctx.beginPath();
  ctx.moveTo(cx - d, cy - d);
  ctx.lineTo(cx + d, cy + d);
  ctx.moveTo(cx + d, cy - d);
  ctx.lineTo(cx - d, cy + d);
  ctx.stroke();
}

/**
 * Wreck by S-57 hazard CATEGORY (CATWRK), matched to NP5011 / Chart No.1 / S-52.
 *   'dangerous'    — Paper/Current: hatched-oval wreck inside a dotted danger
 *                    line (K23/K28); ECDIS: the magenta isolated-danger mark.
 *   'nondangerous' — Paper/Current: hatched-oval wreck (K22); ECDIS: a dotted
 *                    oval (submerged wreck, depth > safety).
 *   'hull'         — solid hull silhouette (K24), all sets.
 *   'masts'        — masts-only symbol (K25), all sets.
 * Depth over a wreck is drawn by the separate l-wreck-sounding text layer.
 * Ink: Paper/ECDIS black, Current magenta (our look).
 */
function drawWreck(ctx: Ctx, style: GlyphStyle, category: string) {
  const cx = CX;
  const cy = S / 2;
  const ink = style === 'current' ? LIGHTMAG : BLACK;
  if (category === 'hull') {
    wreckHull(ctx, cx, cy, ink);
    return;
  }
  if (category === 'masts') {
    wreckMastsOnly(ctx, cx, cy, ink);
    return;
  }
  if (style === 'ecdis') {
    if (category === 'nondangerous') wreckDottedOval(ctx, cx, cy, 11, 7, GREY);
    else isolatedDangerMark(ctx, cx, cy, 9); // dangerous / fallback
    return;
  }
  // paper + current: the hatched-oval submerged-wreck mark; dangerous adds the ring
  wreckHatchedOval(ctx, cx, cy, ink);
  if (category !== 'nondangerous') wreckDottedOval(ctx, cx, cy, 14, 9, ink);
}

/**
 * Generic obstruction shown as a point (S-57 OBSTRN, depth not stated). The
 * 'ecdis' set draws the genuine S-52 OBSTRN01 asset (a magenta-ringed disc); the
 * canvas fallback here is a simple dotted danger circle with a centre dot, the
 * same idea in our own line, so the 'current' set reads it as an obstruction
 * rather than a rock.
 */
function drawObstruction(ctx: Ctx, style: GlyphStyle): void {
  const cx = S / 2;
  const cy = S / 2;
  const r = 9;
  const ink = style === 'ecdis' ? LIGHTMAG : BLACK;
  ctx.save();
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;
  ctx.lineWidth = 1.4;
  // dotted danger ring
  ctx.setLineDash([1.5, 2.5]);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  // centre dot (the charted position)
  ctx.beginPath();
  ctx.arc(cx, cy, 1.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Render one symbol to raw RGBA for map.addImage / map.updateImage. */
function renderWith(
  paint: (ctx: Ctx) => void,
): { width: number; height: number; data: Uint8Array } {
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, S, S);
    paint(ctx);
  }
  const buf = ctx ? ctx.getImageData(0, 0, S, S).data.buffer : new ArrayBuffer(S * S * 4);
  return { width: S, height: S, data: new Uint8Array(buf) };
}

function render(category: string, style: GlyphStyle): { width: number; height: number; data: Uint8Array } {
  return renderWith((ctx) => draw(ctx, category, style));
}

function renderBeacon(category: string): { width: number; height: number; data: Uint8Array } {
  return renderWith((ctx) => drawBeacon(ctx, category));
}

/**
 * Traffic-flow arrow for a fairway / traffic route (S-57 FAIRWY carrying a TRAFIC
 * attribute). Drawn pointing north (up); the symbol layer spins it to the lane's
 * ORIENT, so it always points the way the traffic runs. A one-way lane gets a
 * single head; a two-way lane (TRAFIC = 4) gets a head at each end, so we can read
 * the flow at a glance without tapping anything. Magenta in every symbol set,
 * which is the standard traffic-route ink on both paper and ECDIS.
 */
function drawTrafficArrow(ctx: Ctx, twoWay: boolean): void {
  const cx = S / 2;
  const top = 8;
  const bot = S - 8;
  ctx.save();
  ctx.strokeStyle = LIGHTMAG;
  ctx.fillStyle = LIGHTMAG;
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // The shaft.
  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(cx, bot);
  ctx.stroke();
  const w = 6;
  const h = 9;
  // Top head, pointing up (north before rotation).
  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(cx - w, top + h);
  ctx.lineTo(cx + w, top + h);
  ctx.closePath();
  ctx.fill();
  if (twoWay) {
    // Bottom head, pointing down: the return lane.
    ctx.beginPath();
    ctx.moveTo(cx, bot);
    ctx.lineTo(cx - w, bot - h);
    ctx.lineTo(cx + w, bot - h);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Wire the on-demand symbol supplier. Idempotent-ish: safe to call once per map.
 * Survives palette swaps (the listener stays on the map across setStyle), so the
 * regenerated symbols reappear after a day/dusk/night change. Buoyage is drawn in
 * the current glyph style; setChartSymbolStyle swaps it live.
 */
export function installChartSymbols(map: MaplibreMap): void {
  preloadStandardAssets(map);
  map.on('styleimagemissing', (e) => {
    const id = e.id;
    if (map.hasImage(id)) return;
    const std = standardGlyph(id, currentGlyphStyle);
    if (std) {
      map.addImage(id, std);
      return;
    }
    if (id.startsWith('chart-mark-')) {
      map.addImage(id, render(id.slice('chart-mark-'.length), currentGlyphStyle));
    } else if (id.startsWith('chart-light-')) {
      map.addImage(id, renderWith((ctx) => drawFlare(ctx, id.slice('chart-light-'.length), currentGlyphStyle)));
    } else if (id === 'chart-lighthouse') {
      map.addImage(id, renderWith((ctx) => drawLighthouse(ctx, currentGlyphStyle)));
    } else if (id === 'chart-anchorage') {
      map.addImage(id, renderWith((ctx) => drawAnchor(ctx, currentGlyphStyle)));
    } else if (id === 'chart-traffic-arrow') {
      map.addImage(id, renderWith((ctx) => drawTrafficArrow(ctx, false)));
    } else if (id === 'chart-traffic-arrow-two') {
      map.addImage(id, renderWith((ctx) => drawTrafficArrow(ctx, true)));
    } else if (id.startsWith('chart-wreck-')) {
      const cat = id.slice('chart-wreck-'.length);
      map.addImage(id, renderWith((ctx) => drawWreck(ctx, currentGlyphStyle, cat)));
    } else if (id === 'chart-rock-awash') {
      map.addImage(id, renderWith((ctx) => drawRockAwash(ctx, currentGlyphStyle)));
    } else if (id === 'chart-rock-sub') {
      map.addImage(id, renderWith((ctx) => drawRockSub(ctx, currentGlyphStyle)));
    } else if (id === 'chart-obstruction') {
      map.addImage(id, renderWith((ctx) => drawObstruction(ctx, currentGlyphStyle)));
    } else if (id.startsWith('chart-beacon-')) {
      // Beacons are FIXED marks: draw the beacon glyph (upright structure +
      // topmark), never the floating buoy body. The ECDIS set overrides this with
      // the genuine S-52 beacon asset (standardGlyph, checked above).
      map.addImage(id, renderBeacon(id.slice('chart-beacon-'.length)));
    }
  });
}

/**
 * Switch the buoyage symbology theme live. Re-renders every already-loaded
 * `chart-mark-*` image (remove + re-add under the same id, so the symbol layers
 * re-resolve the glyph and we don't touch the layer specs). The light flares and
 * the lighthouse (major light) are re-skinned too; the anchor is theme-
 * independent for now.
 */
export function setChartSymbolStyle(map: MaplibreMap, style: GlyphStyle, attempt = 0): void {
  // Always record the active style so the on-demand styleimagemissing handler
  // draws the right glyphs from now on.
  currentGlyphStyle = style;
  // map.listImages() throws "Style is not done loading" if called before the
  // style is ready (e.g. on first mount, or just after a setStyle), which would
  // crash the map. Rather than bail permanently (which would skip the re-skin and
  // neuter the symbol-set switch), DEFER: redo it once the style settles, reading
  // the latest style from the module var so the most recent switch wins.
  if (!isStyleReady(map)) {
    // The style can be mid-load when a switch arrives. The live position sim keeps
    // the map busy, so `map.once('idle')` can be starved and never fire, which
    // would leave the re-skin permanently deferred (the symbol set looking stuck).
    // Poll isStyleLoaded instead, reading the latest style from the module var so
    // the most recent switch wins; bounded so a torn-down map can't leak a timer.
    if (attempt < 50) {
      setTimeout(() => setChartSymbolStyle(map, currentGlyphStyle, attempt + 1), 120);
    }
    return;
  }
  for (const id of map.listImages()) {
    let img: { width: number; height: number; data: Uint8Array } | null = null;
    if (id.startsWith('chart-mark-')) {
      img = render(id.slice('chart-mark-'.length), currentGlyphStyle);
    } else if (id.startsWith('chart-light-')) {
      img = renderWith((ctx) => drawFlare(ctx, id.slice('chart-light-'.length), currentGlyphStyle));
    } else if (id === 'chart-lighthouse') {
      img = renderWith((ctx) => drawLighthouse(ctx, currentGlyphStyle));
    } else if (id.startsWith('chart-wreck-')) {
      img = renderWith((ctx) => drawWreck(ctx, currentGlyphStyle, id.slice('chart-wreck-'.length)));
    } else if (id === 'chart-rock-awash') {
      img = renderWith((ctx) => drawRockAwash(ctx, currentGlyphStyle));
    } else if (id === 'chart-rock-sub') {
      img = renderWith((ctx) => drawRockSub(ctx, currentGlyphStyle));
    } else if (id === 'chart-anchorage') {
      img = renderWith((ctx) => drawAnchor(ctx, currentGlyphStyle));
    } else if (id === 'chart-obstruction') {
      img = renderWith((ctx) => drawObstruction(ctx, currentGlyphStyle));
    } else if (id.startsWith('chart-beacon-')) {
      img = renderBeacon(id.slice('chart-beacon-'.length));
    }
    // A genuine standard asset (S-52 / NOAA) takes precedence when available.
    const std = standardGlyph(id, currentGlyphStyle);
    if (std) img = std;
    if (!img) continue;
    // Remove + re-add rather than updateImage. updateImage patches the sprite
    // atlas in place, which repaints but does NOT force already-placed symbols to
    // re-resolve their icon; while the live position sim keeps the map busy a
    // switch could silently fail to take (the look staying on the old set). A
    // remove-then-add marks the image missing-then-available, so the symbol layers
    // re-resolve the glyph for certain. Both calls are synchronous here, so there
    // is no missing-image frame in between (no flicker).
    if (map.hasImage(id)) map.removeImage(id);
    map.addImage(id, img);
  }
  map.triggerRepaint();
}


/**
 * Catalogue / verification render entry. Draws any one glyph onto a provided
 * context using the SAME internal draw functions the map uses, so the symbol
 * comparison sheet shows our real production rendering (no drift). Not used by the
 * app at runtime; kept for the symbol-catalogue tooling.
 */
export function catalogueRender(ctx: Ctx, kind: string, style: GlyphStyle, arg?: string): void {
  switch (kind) {
    case 'mark':
      draw(ctx, arg || 'special', style);
      break;
    case 'flare':
      drawFlare(ctx, arg || 'W', style);
      break;
    case 'lighthouse':
      drawLighthouse(ctx, style);
      break;
    case 'anchor':
      drawAnchor(ctx, style);
      break;
    case 'wreck':
      drawWreck(ctx, style, arg || 'dangerous');
      break;
    case 'rock-awash':
      drawRockAwash(ctx, style);
      break;
    case 'rock-sub':
      drawRockSub(ctx, style);
      break;
    case 'obstruction':
      drawObstruction(ctx, style);
      break;
    case 'beacon':
      draw(ctx, arg || 'special', style);
      break;
    default:
      break;
  }
}
